import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Star, Check, X, Trash2, Copy, MessageSquareQuote } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function StarRow({ n }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-700"}`} />
      ))}
    </div>
  );
}

const STATUS_STYLE = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  rejected: "bg-slate-700/40 text-slate-400 border-slate-600/40",
};

export default function ReviewsAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], aggregate: { avg: 0, count: 0, pending: 0, min_public: 3 } });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reviews");
      setData(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    try {
      if (verb === "delete") await api.delete(`/reviews/${id}`);
      else await api.post(`/reviews/${id}/${verb}`);
      toast.success(verb === "approve" ? "Review published" : verb === "reject" ? "Review hidden" : "Review deleted");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const shareLink = `${window.location.origin}/review/${user?.workspace_id || ""}`;
  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Review link copied"); };

  const agg = data.aggregate || {};
  const filtered = (data.reviews || []).filter((r) => (tab === "all" ? true : r.status === tab));
  const tabs = [
    ["pending", "Pending", (data.reviews || []).filter((r) => r.status === "pending").length, "pending"],
    ["approved", "Published", (data.reviews || []).filter((r) => r.status === "approved").length, "published"],
    ["all", "All", (data.reviews || []).length, "all"],
  ];

  return (
    <div className="space-y-6" data-testid="reviews-admin-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Customer Reviews</h1>
          <p className="mt-1 text-sm text-slate-400">Approve reviews to publish them on your website with star ratings.</p>
        </div>
        <button data-testid="copy-review-link-btn" onClick={copyLink} className="inline-flex items-center gap-2 self-start rounded-full border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-cyan-500">
          <Copy className="h-4 w-4" /> Copy review link
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="card-tac p-5" data-testid="agg-rating">
          <p className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Average rating</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="font-display text-3xl font-black text-white">{agg.avg?.toFixed ? agg.avg.toFixed(1) : agg.avg || 0}</span>
            <StarRow n={Math.round(agg.avg || 0)} />
          </div>
        </div>
        <div className="card-tac p-5">
          <p className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Published reviews</p>
          <p className="mt-2 font-display text-3xl font-black text-white">{agg.count || 0}</p>
        </div>
        <div className="card-tac p-5">
          <p className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Awaiting approval</p>
          <p className="mt-2 font-display text-3xl font-black text-amber-400">{agg.pending || 0}</p>
        </div>
      </div>

      {agg.count < (agg.min_public || 3) && (
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-4 py-3 text-sm text-cyan-200" data-testid="schema-hint">
          Star ratings appear in Google search results once you have {agg.min_public || 3} published reviews. You have {agg.count || 0}.
        </div>
      )}

      <div className="flex gap-2">
        {tabs.map(([key, label, n, tid]) => (
          <button key={key} data-testid={`reviews-tab-${tid}`} onClick={() => setTab(key)}
            className={`rounded-full px-4 py-1.5 text-sm ${tab === key ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            {label} {n ? `(${n})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="py-16 text-center text-slate-500">Loading reviews...</p>
      ) : filtered.length === 0 ? (
        <div className="card-tac flex flex-col items-center gap-3 py-16 text-center" data-testid="reviews-empty">
          <MessageSquareQuote className="h-10 w-10 text-slate-600" />
          <p className="text-slate-400">No {tab === "all" ? "" : tab} reviews yet.</p>
          <p className="max-w-sm text-sm text-slate-500">Share your review link with happy customers, or reviews will come in automatically when you complete jobs.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.id} className="card-tac p-5" data-testid={`review-card-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <StarRow n={r.rating} />
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                  </div>
                  <p className="mt-2 font-semibold text-white">{r.author_name}{r.author_company ? ` · ${r.author_company}` : ""}</p>
                  {r.comment && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-300">{r.comment}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {r.status !== "approved" && (
                    <button data-testid={`approve-${r.id}`} onClick={() => act(r.id, "approve")} title="Publish"
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/25">
                      <Check className="h-4 w-4" /> Publish
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button data-testid={`reject-${r.id}`} onClick={() => act(r.id, "reject")} title="Hide"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500">
                      <X className="h-4 w-4" /> Hide
                    </button>
                  )}
                  <button data-testid={`delete-${r.id}`} onClick={() => act(r.id, "delete")} title="Delete"
                    className="inline-flex items-center rounded-full border border-slate-700 p-1.5 text-slate-400 hover:border-red-500 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
